/**
 * Usage-credit amount sign helpers — EB-3883 (sign-handling addendum).
 *
 * Credits carry a conventional sign per type: General is positive, Expired is
 * negative. These pure helpers let the Add-Credit dialog proactively set that
 * sign and detect a deliberate deviation (which is then confirmed on submit).
 * Amounts are locale-decimal strings (comma or dot) — see `locale-decimal.util`.
 */
import { CREDIT_TYPE_EXPIRED } from './usage-credit.models';

/** Conventional sign for a credit type: Expired → −1, everything else → +1. */
export function expectedSignForType(type: number): 1 | -1 {
  return type === CREDIT_TYPE_EXPIRED ? -1 : 1;
}

/** True when the raw amount string starts with an explicit `+`/`−` (operator chose a sign). */
export function hasExplicitSign(raw: string): boolean {
  return /^\s*[+-]/.test(raw ?? '');
}

/**
 * Apply a sign to a locale-decimal amount string, preserving the magnitude text
 * (comma/dot, decimals) — only the leading sign changes. A blank or sign-only
 * input has no magnitude to sign, so it returns `''`.
 */
export function applySignToDecimalString(raw: string, sign: 1 | -1): string {
  const trimmed = (raw ?? '').trim();
  const magnitude = trimmed.replace(/^[+-]\s*/, '').trim();
  if (magnitude.length === 0) return '';
  return sign < 0 ? `-${magnitude}` : magnitude;
}

/**
 * True when the parsed (signed) amount matches the type's conventional sign.
 * Zero is treated as matching — it is rejected by the non-zero validator, not
 * a sign concern.
 */
export function amountSignMatchesType(type: number, parsedAmount: number): boolean {
  if (parsedAmount === 0) return true;
  return Math.sign(parsedAmount) === expectedSignForType(type);
}
